import { Main, Page } from '../../src/components/Layout';
import React, { PureComponent } from 'react';

import Breadcrumb from '../../src/components/Breadcrumb';
import FacebookEvent from '../../src/components/PageBuilderBlocks/FacebookEvent';
import Head from 'next/head';
import Header from '../../src/components/Header';
import ImageGallery from '../../src/components/ImageGallery/ImageGallery';
import Informations from '../../src/components/Informations';
import InstagramFeed from '../../src/components/Feed/InstagramFeed';
import More from './more';
import { Query } from 'react-apollo';
import { ShareButton } from '../../src/components/Button';
import Time from '../../src/components/Time';
import VideoGallery from '../../src/components/VideoGallery/VideoGallery';
import Wysiwyg from '../../src/components/Wysiwyg';
import createContext from '../../src/utils/format/createContext';
import getEvent from '../../src/apollo/graphql/event/getEvent';
import getEventsByCategory from '../../src/apollo/graphql/event/getEventsByCategory';
import getMore from '../../src/utils/collection/getMore';
import { media } from '../../src/styles/media';
import styled from 'styled-components';
import { withI18N } from '../../src/hoc/index';

const Content = styled.div`
  ${media.desktop`
    margin: 0 auto;
    max-width: 790px;
  `};
`;

const Container = styled.div`
  position: relative;
  display: block;
  margin: 0 auto;
  padding: 0 ${props => props.theme.layout.gutter * 1.5}px;
  width: 100%;
  max-width: ${props => props.theme.layout.width}px;
`;

const Introduction = styled.div`
  padding: 0 0 3rem 0;

  ${media.tablet`
    display: flex;
    padding: 4rem 0;
  `};
`;

const ImageContainer = styled.div`
  position: relative;

  ${media.tablet`
    flex: 0.60;
  `};
`;

const TimeWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);

  ${media.tablet`
    top: 0;
    left: 0;
    transform: none;
  `};
`;

const Image = styled.img`
  display: none;
  object-fit: cover;
  font-family: 'object-fit: cover;';

  ${media.tablet`
    display: block;
    height: 100%;
    width: 100%;
  `};
`;

const InformationContainer = styled.div`
  flex: 1;
  padding: 2rem 0 0 6rem;

  h2 {
    color: ${props => props.theme.color.brown};
    font-size: 2rem;
    line-height: 2.35rem;
    letter-spacing: 1px;
    margin-bottom: 2rem;

    ${media.tablet`
      font-size: 2.4rem;
      line-height: 3.5rem;
    `};
  }

  p {
    color: ${props => props.theme.color.darkGrey};
    font-family: ${props => props.theme.font.regular};
    font-size: 1.8rem;
    font-style: italic;
    line-height: 2.2rem;

    ${media.tablet`
      font-style: normal;
      line-height: 2.4rem;
    `};
  }

  ${media.tablet`
    flex: 1;
    padding: 2rem 0 0 6rem;
  `};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 4rem;

  ${media.tablet`
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: 8rem;
  `};

  ${media.desktop`
    margin: 0 auto;
    max-width: 790px;
  `};
`;

const ShareContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto;

  ${media.tablet`
    margin: 0 auto;
  `};
`;

class Event extends PureComponent {
  static async getInitialProps({ query, i18n }) {
    return {
      slug: query && query.slug,
      i18n
    };
  }

  renderHeader = event => (
    <Header
      title={event.detail.name}
      subtitle={event.category.detail.label}
      image={event.header_desktop}
      search={false}
    />
  );

  renderBreadcrumb = event => (
    <Breadcrumb
      items={[
        { href: '/', label: this.props.t('common:nav.tabs.home') },
        { label: event.detail.name }
      ]}
    />
  );

  renderIntroduction = event => (
    <Introduction>
      <ImageContainer>
        <TimeWrapper>
          <Time date={event.detail.date} />
        </TimeWrapper>
        <Image src={event.visual_desktop} />
      </ImageContainer>
      <InformationContainer>
        <h2>{event.detail.introduction}</h2>
      </InformationContainer>
    </Introduction>
  );

  renderMore = event => {
    const { i18n } = this.props;
    return (
      <Query
        query={getEventsByCategory}
        variables={{ categoryId: event.category.id }}
        context={createContext(i18n.language)}
        fetchPolicy="no-cache"
      >
        {({ loading, error, data }) => {
          if (!loading && !error && data && data.getEventsByCategory) {
            const events = getMore(data.getEventsByCategory, event, 3);
            return events.length > 0 && <More events={events} />;
          }

          return null;
        }}
      </Query>
    );
  };

  renderBlock(block, key) {
    if (block?.slides?.length < 1) return null;

    switch (block?.type) {
      case 'video_gallery_block':
        return (
          <Container key={key}>
            <VideoGallery data={block} />
          </Container>
        );
      case 'image_gallery_block':
        return (
          <Container key={key}>
            <ImageGallery data={block} />
          </Container>
        );
      default:
        return null;
    }
  }

  renderPage = event => {
    const blockVideo = event?.blocks?.find(
      block => block.type === 'video_gallery_block'
    );
    const blockImage = event?.blocks?.find(
      block => block.type === 'image_gallery_block'
    );

    return (
      <div>
        <Head>
          <title>{event.detail.name}</title>
          <meta
            name="description"
            content={event.detail.meta_og_description}
            key="description"
          />
          <meta property="og:title" content={event.detail.meta_og_title} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={event.detail.meta_og_url} />
          <meta
            property="og:description"
            content={event.detail.meta_og_description}
          />
        </Head>
        {this.renderHeader(event)}
        <Main>
          <Container>
            {this.renderBreadcrumb(event)}
            <Content>
              {this.renderIntroduction(event)}
              <Wysiwyg content={event.detail.content} />
            </Content>
            {blockImage && this.renderBlock(blockImage, 'block_image')}
            {blockVideo && this.renderBlock(blockVideo, 'block_video')}
            {event.detail?.fb_title && (
              <FacebookEvent
                image={event.fb_cover}
                title={event.detail.fb_title}
                label={event.detail.fb_cta_label}
                url={event.detail.fb_cta_url}
                date={event.detail.fb_date}
                location={event.detail.fb_location}
              />
            )}
            <Footer>
              <ShareContainer>
                <ShareButton
                  label={this.props.t('common:events.share')}
                  type="facebook"
                  href={`https://www.facebook.com/sharer.php?u=${
                    event.detail.meta_og_url
                  }`}
                />
                <ShareButton
                  label={this.props.t('common:events.share')}
                  type="twitter"
                  href={`https://twitter.com/intent/tweet?url=${
                    event.detail.meta_og_url
                  }&text=${event.detail.meta_og_title}`}
                />
              </ShareContainer>
            </Footer>
          </Container>
          <Informations event={event} />
          {this.renderMore(event)}
          <InstagramFeed title={event.detail?.section_instagram_title} />
        </Main>
      </div>
    );
  };

  render() {
    const { slug, i18n } = this.props;

    return (
      <Page event>
        <Query
          query={getEvent}
          variables={{ slug }}
          context={createContext(i18n.language)}
          fetchPolicy="no-cache"
        >
          {({ loading, error, data }) => {
            if (!loading && !error && data && data.getEvent) {
              const event = data.getEvent;
              loading && <div>Loading ...</div>;
              error && <div>Error ! {error.message}</div>;
              return this.renderPage(event);
            }

            return null;
          }}
        </Query>
      </Page>
    );
  }
}

export default withI18N()(Event);
